// BizPlus parity Phase 4.1 — the operational dashboard panel.
//
// KPI tiles (each linking to its pre-filtered list), a My/Team/All scope
// selector, overdue and due-today followups, a reorder alert banner, and two
// 10-day trend charts.
//
// A tile the server reports as unavailable renders as "—" with a reason, never
// as a zero. A dashboard that shows an outage as "0 open quotations" is quietly
// believed, which is worse than one that admits it could not load.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, PackageX, Clock, CalendarClock, TrendingUp, ArrowRight,
} from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { formatINR } from '@/shared/utils/currency';

type Scope = 'mine' | 'team' | 'all';

interface Tile {
  key: string; label: string; link: string;
  available: boolean; reason?: string;
  count?: number; value?: number | null;
}
interface TrendPoint { date: string; count: number }
interface KpiResponse {
  scope: Scope;
  tiles: Tile[];
  trends: { leads?: TrendPoint[]; quotations?: TrendPoint[]; error?: string };
}
interface ReorderItem { _id: string; name: string; sku?: string; alertQuantity: number; available: number }
interface FollowupRow {
  _id: string; parentType: string; followupNote?: string; details?: string;
  followupDate: string; contactPhone?: string; representativeId?: string;
}

const SCOPES: { key: Scope; label: string }[] = [
  { key: 'mine', label: 'Mine' },
  { key: 'team', label: 'Team' },
  { key: 'all', label: 'All' },
];

/** Raw SVG sparkline — avoids pulling a chart library in for two series. */
function Sparkline({ points }: { points: TrendPoint[] }) {
  if (!points.length) return null;
  const max = Math.max(1, ...points.map((p) => p.count));
  const w = 100;
  const h = 28;
  const step = points.length > 1 ? w / (points.length - 1) : w;
  const path = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - (p.count / max) * h).toFixed(1)}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-8 w-full" preserveAspectRatio="none" role="img"
      aria-label={`${points.length}-day trend, peak ${max}`}>
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function daysOverdue(iso: string) {
  const then = new Date(iso);
  then.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((now.getTime() - then.getTime()) / 86400000));
}

export function OperationalKpiPanel() {
  const [scope, setScope] = useState<Scope>('mine');

  const kpi = useQuery({
    queryKey: ['dashboard-kpi', scope],
    queryFn: async () => (await api.get<KpiResponse>(`/api/dashboard-kpi?scope=${scope}`)).data,
  });

  const reorder = useQuery({
    queryKey: ['dashboard-reorder'],
    queryFn: async () => (await api.get<{ items: ReorderItem[]; gated?: boolean }>('/api/dashboard-kpi/reorder-alert')).data,
  });

  const overdue = useQuery({
    queryKey: ['followups-overdue', scope],
    queryFn: async () => (await api.get<{ followups: FollowupRow[]; count: number }>(
      `/api/followups/overdue?scope=${scope}`,
    )).data,
  });

  const dueToday = useQuery({
    queryKey: ['followups-due-today', scope],
    queryFn: async () => (await api.get<{ followups: FollowupRow[]; count: number }>(
      `/api/followups/due-today?scope=${scope}`,
    )).data,
  });

  const tiles = kpi.data?.tiles ?? [];
  const reorderItems = reorder.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-body-md font-semibold uppercase tracking-wide text-muted-foreground">
          Today at a glance
        </h2>
        <div className="flex gap-1">
          {SCOPES.map((s) => (
            <Button key={s.key} size="sm" variant={scope === s.key ? 'secondary' : 'ghost'}
              onClick={() => setScope(s.key)}>
              {s.label}
            </Button>
          ))}
        </div>
      </div>

      {/* The server may narrow the requested scope; say so rather than showing
          "Mine" selected while displaying team data. */}
      {kpi.data && kpi.data.scope !== scope && (
        <p className="text-body-sm text-muted-foreground">
          Showing <strong>{kpi.data.scope}</strong> — your role does not include the “{scope}” view.
        </p>
      )}

      {reorderItems.length > 0 && (
        <Card>
          <CardBody className="flex flex-wrap items-center gap-3 text-body-md">
            <PackageX className="h-5 w-5 shrink-0 text-warning-text" />
            <span className="font-medium">
              {reorderItems.length} product{reorderItems.length > 1 ? 's' : ''} at or below reorder level
            </span>
            <span className="text-muted-foreground">
              {reorderItems.slice(0, 3).map((i) => `${i.name} (${i.available}/${i.alertQuantity})`).join(' · ')}
              {reorderItems.length > 3 ? ` +${reorderItems.length - 3} more` : ''}
            </span>
            <Link to="/inventory" className="ml-auto text-primary hover:underline">
              Open inventory <ArrowRight className="inline h-3.5 w-3.5" />
            </Link>
          </CardBody>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {kpi.isLoading
          ? <div className="contents" role="status" aria-busy="true" aria-live="polite">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-24 animate-pulse rounded-card border border-border" />)}</div>
          : tiles.map((tile) => (
            <Link key={tile.key} to={tile.link}
              className="group rounded-card border border-border bg-card p-4 shadow-surface-1 transition hover:border-primary/30 hover:shadow-surface-2">
              <p className="flex items-center justify-between text-body-sm font-medium text-on-surface-variant">
                {tile.label}
                <ArrowRight className="h-3.5 w-3.5 text-on-surface-subtle transition group-hover:translate-x-0.5 group-hover:text-primary" aria-hidden />
              </p>
              {tile.available ? (
                <>
                  <p className="mt-1.5 font-mono text-[22px] font-semibold leading-tight tabular-nums text-on-surface">
                    {tile.count?.toLocaleString('en-IN')}
                  </p>
                  {/* Value only where the collection genuinely holds money. */}
                  {tile.value !== null && tile.value !== undefined && (
                    <p className="mt-0.5 text-body-sm text-on-surface-variant">{formatINR(tile.value)}</p>
                  )}
                </>
              ) : (
                <>
                  <p className="mt-1 font-mono text-data-lg font-semibold text-on-surface-variant">—</p>
                  <p className="mt-0.5 flex items-center gap-1 text-body-sm text-warning-text">
                    <AlertTriangle className="h-3.5 w-3.5" /> {tile.reason || 'Unavailable'}
                  </p>
                </>
              )}
            </Link>
          ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <FollowupList
          title="Overdue followups"
          icon={<Clock className="h-4 w-4 text-danger-text" />}
          rows={overdue.data?.followups ?? []}
          loading={overdue.isLoading}
          emptyText="Nothing overdue."
          showDaysOverdue
        />
        <FollowupList
          title="Due today"
          icon={<CalendarClock className="h-4 w-4 text-info-text" />}
          rows={dueToday.data?.followups ?? []}
          loading={dueToday.isLoading}
          emptyText="Nothing due today."
        />
      </div>

      {kpi.data?.trends && !kpi.data.trends.error && (
        <div className="grid gap-4 sm:grid-cols-2">
          {([
            ['Enquiries created', kpi.data.trends.leads ?? []],
            ['Quotations created', kpi.data.trends.quotations ?? []],
          ] as const).map(([label, series]) => (
            <Card key={label}>
              <CardBody>
                <div className="flex items-center justify-between">
                  <p className="text-body-sm font-medium text-on-surface-variant">{label}</p>
                  <span className="flex items-center gap-1 text-body-sm text-muted-foreground">
                    <TrendingUp className="h-3.5 w-3.5" /> last 10 days
                  </span>
                </div>
                <p className="mt-1 text-headline-md font-semibold tabular-nums">
                  {series.reduce((s, p) => s + p.count, 0)}
                </p>
                <div className="mt-2 text-primary"><Sparkline points={series} /></div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function FollowupList({ title, icon, rows, loading, emptyText, showDaysOverdue = false }: {
  title: string; icon: React.ReactNode; rows: FollowupRow[];
  loading: boolean; emptyText: string; showDaysOverdue?: boolean;
}) {
  return (
    <Card>
      {/* CardHeader.title is already ReactNode, so the icon goes inline. */}
      <CardHeader title={<span className="flex items-center gap-2">{icon}{title} ({rows.length})</span>} />
      <CardBody className="p-0">
        {loading ? (
          <div className="p-4"><Skeleton className="h-24" /></div>
        ) : !rows.length ? (
          <p className="p-6 text-center text-body-md text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="divide-y">
            {rows.slice(0, 8).map((f) => (
              <li key={f._id} className="flex items-start gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-body-md font-medium">{f.followupNote || '(no note)'}</p>
                  <p className="text-body-sm text-muted-foreground">
                    {f.parentType}
                    {f.contactPhone ? ` · ${f.contactPhone}` : ''}
                    {' · '}{new Date(f.followupDate).toLocaleDateString('en-IN')}
                  </p>
                </div>
                {showDaysOverdue && (
                  <span className="shrink-0 rounded-full bg-danger/10 px-2 py-0.5 text-body-sm font-medium text-danger-text">
                    {daysOverdue(f.followupDate)}d
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
